/* ROYDED Full Stack upgrade drawer.
   Turns the loose top, cap and shorts in the cart into the native Full Stack bundle variant.
   API: window.RDFullStackUpgrade.open() and .close(). open() returns false when the sheet is not on the page
   (callers then fall back to the product page), otherwise a promise. The sheet slides in at once and fills in
   when /cart.js answers. With 2 or more loose tops it uses the most recently added one (/cart.js lists the newest line first).
   Upgrade is one /cart/update.js call (adds the bundle variant, takes one unit off each loose line, returns the
   rendered cart sections), so the cart drawer repaints from that single reply. The offer properties are set on the
   new line straight after in the background. If the cart already holds that bundle variant it falls back to
   /cart/add.js (with the properties) then /cart/update.js so the two lines never merge into the wrong quantity.
   Edit mode (RDFullStackUpgrade.edit): the same sheet opened from a Full Stack line in the cart to change the top
   colour, top size or shorts size. Save is one /cart/update.js: old line to 0, new bundle variant added with the same
   quantity, offer properties copied across. The x on each piece of a Full Stack line splits the bundle into its two other
   pieces with one /cart/update.js (bundle line to 0, the other two variants added with the bundle's quantity). */
(function () {
  'use strict';
  if (window.RDFullStackUpgrade) return;

  var OPEN_MS = 250;
  var CLOSE_MS = 200;
  var REQUEST_TIMEOUT = 15000;
  var root = null;
  var lockDepth = 0;
  var prefetched = null;
  var phoneQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(cents) {
    var n = Math.round(Number(cents || 0)) / 100;
    return '$' + (n % 1 ? n.toFixed(2) : String(n));
  }
  function uuid() {
    try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (e) { /* fall through */ }
    return 'rdfu-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  // Superset tiers come from snippets/rd-superset-tiers.liquid ("2:15,3:30,4:50", NZD dollars), the same source the cart offers use.
  function parseTiers(value) {
    return String(value || '').split(',').map(function (pair) {
      var parts = pair.split(':');
      return { qty: parseInt(parts[0], 10), off: Math.round(parseFloat(parts[1]) * 100) };
    }).filter(function (tier) {
      return tier.qty > 0 && tier.off >= 0;
    }).sort(function (a, b) { return a.qty - b.qty; });
  }
  function supersetSaving(tiers, count) {
    var off = 0;
    tiers.forEach(function (tier) { if (count >= tier.qty) off = tier.off; });
    return off;
  }
  function routes(name, fallback) { return (window.routes && window.routes[name]) || fallback; }
  // The JSON cart endpoints (add.js, update.js, change.js), honouring locale-prefixed routes.
  function ajaxUrl(name, fallback) {
    var url = routes(name, fallback);
    return /\.js$/.test(url) ? url : url + '.js';
  }

  // fetch + JSON with a timeout, so a stalled request ends in an error message instead of a hang.
  function json(url, options, timeout) {
    options = options || {};
    var controller = window.AbortController ? new AbortController() : null;
    var timer = null;
    if (controller) {
      options.signal = controller.signal;
      timer = window.setTimeout(function () { controller.abort(); }, timeout || REQUEST_TIMEOUT);
    }
    var done = function () { if (timer) window.clearTimeout(timer); };
    return fetch(url, options).then(function (response) {
      return response.json().then(function (body) {
        if (!response.ok || body.status >= 400 || body.errors) throw body;
        return body;
      }, function () {
        throw { status: response.status, description: '' };
      });
    }).then(function (body) { done(); return body; }, function (error) { done(); throw error; });
  }
  function post(url, body) {
    return json(url, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
  }
  function errorText(error) {
    if (error && error.name === 'AbortError') return 'That is taking too long. Check your cart, then try again.';
    var text = error && (error.description || error.message);
    if (typeof text === 'string' && text && !/^cart error$/i.test(text)) return text;
    return 'That did not go through. Your cart was refreshed, so check it and try again.';
  }

  // A fresh /cart.js read. Hover or touch on an Upgrade button starts one early, so the sheet can fill in at once;
  // an early read is used once and only within 1.5 seconds, so the sheet never shows an old cart.
  function readCartNow() {
    return json(routes('cart_url', '/cart') + '.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }, 10000);
  }
  function takeCart() {
    var early = prefetched && Date.now() - prefetched.at < 1500 ? prefetched.promise : null;
    prefetched = null;
    return early || readCartNow();
  }
  function prefetchCart(event) {
    var target = event.target;
    if (!target || !target.closest || !target.closest('[data-rd-oc-upgrade],[data-rdfu-change],[data-rdfu-split]')) return;
    if (prefetched && Date.now() - prefetched.at < 1500) return;
    var promise = readCartNow();
    prefetched = { at: Date.now(), promise: promise };
    promise.catch(function () { if (prefetched && prefetched.promise === promise) prefetched = null; });
  }
  ['pointerover', 'touchstart', 'focusin'].forEach(function (type) {
    document.addEventListener(type, prefetchCart, { capture: true, passive: true });
  });

  /* ---------- Cart drawer repaint (same sections and steps as rd-cart-upsell.js) ---------- */

  // sections_url must be a real path (an embedded preview frame reports a bare name, which Shopify rejects).
  function sectionsUrl() {
    var path = window.location.pathname || '/';
    return path.charAt(0) === '/' ? path : '/';
  }
  function sectionIds() {
    var ids = document.querySelector('cart-drawer') ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    if (items && items.dataset.id) ids.push(items.dataset.id);
    if (footer && footer.dataset.id) ids.push(footer.dataset.id);
    return ids;
  }
  function replaceSection(selector, html) {
    if (!html) return;
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var target = document.querySelector(selector);
    var source = parsed.querySelector(selector) || parsed.querySelector('.shopify-section');
    if (target && source) target.innerHTML = source.innerHTML;
  }
  function renderCart(payload) {
    var sections = payload && payload.sections;
    if (!sections) return false;
    var items = document.getElementById('main-cart-items');
    var footer = document.getElementById('main-cart-footer');
    var itemsId = items && items.dataset.id;
    var footerId = footer && footer.dataset.id;
    if (sections['cart-drawer']) replaceSection('#CartDrawer', sections['cart-drawer']);
    if (sections['cart-icon-bubble']) replaceSection('#cart-icon-bubble', sections['cart-icon-bubble']);
    if (itemsId && sections[itemsId]) replaceSection('#main-cart-items .js-contents', sections[itemsId]);
    if (footerId && sections[footerId]) replaceSection('#main-cart-footer .js-contents', sections[footerId]);
    var count = typeof payload.item_count === 'number' ? payload.item_count : null;
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      if (count !== null) drawer.classList.toggle('is-empty', count === 0);
      var overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
    }
    var pageFooter = document.getElementById('main-cart-footer');
    if (pageFooter && count !== null) pageFooter.classList.toggle('is-empty', count === 0);
    try {
      if (window.publish && window.PUB_SUB_EVENTS) window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'rd-fullstack-upgrade', cartData: payload });
    } catch (e) { /* subscribers must never block the cart */ }
    try { if (window.RDCartUpsell && typeof window.RDCartUpsell.init === 'function') window.RDCartUpsell.init(); } catch (e) { /* offer cards come back on the next render */ }
    return true;
  }
  // Repaints the drawer from the server (used after a failure). Sections come from a plain GET, not a JSON one.
  function refreshCartDrawer() {
    var url = routes('cart_url', '/cart') + '?sections=' + sectionIds().join(',') + '&sections_url=' + encodeURIComponent(sectionsUrl());
    return Promise.all([
      readCartNow(),
      json(url, { credentials: 'same-origin' }, 10000)
    ]).then(function (r) {
      var cart = r[0];
      cart.sections = r[1];
      renderCart(cart);
      return cart;
    });
  }

  function lockScroll() {
    lockDepth += 1;
    if (lockDepth > 1) return;
    var doc = document.documentElement;
    var gap = window.innerWidth - doc.clientWidth;
    if (gap > 0) doc.style.setProperty('--rdfu-gap', gap + 'px');
    doc.classList.add('rdfu-lock');
  }
  function unlockScroll() {
    if (lockDepth === 0) return;
    lockDepth -= 1;
    if (lockDepth > 0) return;
    var doc = document.documentElement;
    doc.classList.remove('rdfu-lock');
    doc.style.removeProperty('--rdfu-gap');
  }

  function Sheet(el) {
    this.root = el;
    this.dialog = el.querySelector('[data-rdfu-dialog]');
    this.panel = el.querySelector('[data-rdfu-panel]');
    this.scrim = el.querySelector('[data-rdfu-scrim]');
    this.piecesEl = el.querySelector('[data-rdfu-pieces]');
    this.subEl = el.querySelector('[data-rdfu-sub]');
    this.savingEl = el.querySelector('[data-rdfu-saving]');
    this.wasEl = el.querySelector('[data-rdfu-was]');
    this.nowEl = el.querySelector('[data-rdfu-now]');
    this.cta = el.querySelector('[data-rdfu-cta]');
    this.ctaLabel = el.querySelector('[data-rdfu-cta-label]');
    this.ctaPrice = el.querySelector('[data-rdfu-cta-price]');
    this.msgEl = el.querySelector('[data-rdfu-msg]');
    this.liveEl = el.querySelector('[data-rdfu-live]');
    this.isOpen = false;
    this.busy = false;
    this.loading = false;
    this.mode = 'upgrade';
    this.edit = null;
    this.splitting = false;
    this.titleEl = el.querySelector('.rdfu__title');
    this.seq = 0;
    this.inCart = { top: null, cap: null, shorts: null };
    this.cartItems = [];
    this.looseTops = 0;
    this.changing = { top: false, shorts: false };
    this.handlers = [];
    this.data = null;
    try { this.data = JSON.parse(el.querySelector('[data-rdfu-data]').textContent); } catch (e) { this.data = null; }
    if (!this.data || !this.dialog) return;
    this.index();
    this.bind();
  }

  /* ---------- Data ---------- */

  Sheet.prototype.index = function () {
    var d = this.data;
    var self = this;
    this.variants = {}; // variant id -> { kind, price, size, colour, img, p, c }
    this.choices = [];  // every top colour across both tops
    d.tops.forEach(function (product, p) {
      product.colours.forEach(function (colour, c) {
        self.choices.push({ p: p, c: c });
        colour.variants.forEach(function (v) {
          self.variants[String(v.id)] = { kind: 'top', price: v.price, size: v.size, colour: colour.name, img: colour.img, p: p, c: c, available: v.available };
        });
      });
    });
    d.cap.variants.forEach(function (v) {
      self.variants[String(v.id)] = { kind: 'cap', price: v.price, size: v.size, img: d.cap.img, available: v.available };
    });
    d.shorts.variants.forEach(function (v) {
      self.variants[String(v.id)] = { kind: 'shorts', price: v.price, size: v.size, img: d.shorts.img, available: v.available };
    });
    this.capVariant = d.cap.variants[0];
    this.parentBy = {};
    this.rowByBundle = {};
    d.parents.forEach(function (row) { self.parentBy[row[0] + ':' + row[1]] = row; self.rowByBundle[String(row[2])] = row; });
    this.topIds = {};
    d.tops.forEach(function (product) { self.topIds[String(product.id)] = true; });
    this.tiers = parseTiers(d.tiers);
  };

  Sheet.prototype.parentFor = function (teeId, shortsId) {
    var row = this.parentBy[String(teeId) + ':' + String(shortsId)];
    if (!row) return null;
    var bv = this.data.bv[row[2]];
    return { id: row[2], price: bv ? bv[0] : 0, available: !!(bv && bv[1]) };
  };

  // Reads the cart: the newest loose top, the cap and the shorts that can go into the bundle.
  Sheet.prototype.readCart = function (cart) {
    var found = { top: null, cap: null, shorts: null };
    var d = this.data;
    this.looseTops = 0;
    ((cart && cart.items) || []).forEach(function (line) {
      if (!line || line.parent_relationship || line.has_components) return;
      var pid = String(line.product_id);
      if (pid === String(d.bundle.id)) return;
      if (this.topIds[pid]) { this.looseTops += parseInt(line.quantity, 10) || 0; if (!found.top) found.top = line; }
      else if (pid === String(d.cap.id)) { if (!found.cap) found.cap = line; }
      else if (pid === String(d.shorts.id)) { if (!found.shorts) found.shorts = line; }
    }, this);
    this.inCart = found;
    this.cartItems = (cart && cart.items) || [];
  };

  // True when the cart already holds a line of this bundle variant (its quantity would be set, not added to).
  Sheet.prototype.hasBundleLine = function (variantId) {
    return this.cartItems.some(function (line) { return String(line.variant_id) === String(variantId); });
  };

  /* ---------- State ---------- */

  Sheet.prototype.reset = function () {
    this.pickChoice = 0;
    this.pickTop = '';
    this.pickShorts = '';
    this.changing = { top: false, shorts: false };
    if (this.mode === 'edit' && this.edit) {
      // The bundle's current top and shorts start selected.
      var current = this.variants[this.edit.teeId];
      if (current) {
        for (var j = 0; j < this.choices.length; j += 1) {
          if (this.choices[j].p === current.p && this.choices[j].c === current.c) { this.pickChoice = j; break; }
        }
      }
      this.pickTop = this.edit.teeId;
      this.pickShorts = this.edit.shortsId;
    } else {
      var inTop = this.inCart.top && this.variants[String(this.inCart.top.variant_id)];
      if (inTop) {
        for (var i = 0; i < this.choices.length; i += 1) {
          if (this.choices[i].p === inTop.p && this.choices[i].c === inTop.c) { this.pickChoice = i; break; }
        }
      }
    }
    this.setMessage('');
  };

  Sheet.prototype.topId = function () { return this.inCart.top && !this.changing.top ? String(this.inCart.top.variant_id) : this.pickTop; };
  Sheet.prototype.shortsId = function () { return this.inCart.shorts && !this.changing.shorts ? String(this.inCart.shorts.variant_id) : this.pickShorts; };

  // Prices (cents) that fill the footer. Falls back to a reference variant until the sizes are picked.
  Sheet.prototype.pricing = function () {
    var d = this.data;
    var choice = this.choices[this.pickChoice];
    var topId = this.topId();
    var shortsId = this.shortsId();
    var refTop = topId;
    if (!refTop) refTop = String(d.tops[choice.p].colours[choice.c].variants[0].id);
    var refShorts = shortsId || String(d.shorts.variants[0].id);
    var parent = this.parentFor(refTop, refShorts);
    var topInfo = this.variants[refTop];
    var shortsInfo = this.variants[refShorts];
    var capPrice = this.capVariant.price;
    var out = { parent: parent, ready: false, bundle: 0, was: 0, extra: 0, delta: 0, loss: 0, lossKnown: true };
    if (!parent || !topInfo || !shortsInfo) return out;
    out.bundle = parent.price;
    out.was = topInfo.price + capPrice + shortsInfo.price;
    if (this.mode === 'edit' && this.edit) {
      // Edit: ready only when the picks make a different bundle variant; the button shows the price difference.
      var before = d.bv[this.edit.parentId];
      out.delta = parent.price - (before ? before[0] : parent.price);
      out.ready = !!(topId && shortsId && parent.available && String(parent.id) !== String(this.edit.parentId));
      return out;
    }
    // The loose pieces that leave the cart are paid at their own price, even when a different size or colour was picked.
    var paid = 0;
    if (this.inCart.top) { var oldTop = this.variants[String(this.inCart.top.variant_id)]; paid += oldTop ? oldTop.price : topInfo.price; }
    if (this.inCart.cap) paid += capPrice;
    if (this.inCart.shorts) { var oldShorts = this.variants[String(this.inCart.shorts.variant_id)]; paid += oldShorts ? oldShorts.price : shortsInfo.price; }
    // A loose top moving into the bundle can drop the cart to a lower Superset tier: that saving is lost.
    var after = Math.max(0, this.looseTops - (this.inCart.top ? 1 : 0));
    var loss = supersetSaving(this.tiers, this.looseTops) - supersetSaving(this.tiers, after);
    var nzd = !window.Shopify || !Shopify.currency || !Shopify.currency.active || Shopify.currency.active === 'NZD';
    if (loss > 0 && !nzd) loss = Math.round(loss * (Number(Shopify.currency.rate) || 1));
    out.loss = loss;
    out.lossKnown = nzd || loss === 0;
    out.extra = Math.max(0, parent.price - paid + loss);
    out.ready = !!(topId && shortsId && parent.available);
    return out;
  };

  /* ---------- Render ---------- */

  function line(item, fallbackPhoto) {
    return item && item.image ? item.image : fallbackPhoto;
  }

  function inCartStatus(item, info) {
    var bits = [];
    var opts = item && (item.options_with_product || item.variant_options);
    if (opts && opts.length) {
      opts.forEach(function (o) {
        var v = String(o && typeof o === 'object' ? o.value : o || '');
        if (v && !/^default title$/i.test(v)) bits.push(v);
      });
    } else if (info) {
      if (info.colour) bits.push(info.colour);
      if (info.size && !/^default title$/i.test(info.size)) bits.push(info.size);
    }
    if (!bits.length) bits.push('One size');
    return bits.map(esc).join(' &middot; ');
  }

  // Under the name of a piece that is in the cart: its colour and size, then a small text button.
  // Change opens the pickers, Keep mine puts the cart's piece back. No key, no button (the cap).
  Sheet.prototype.statusBlock = function (statusHtml, key, label) {
    return '<p class="rdfu-piece__status">' + statusHtml + '</p>' +
      (key ? '<button type="button" class="rdfu-piece__change" data-pc="' + key + '">' + esc(label) + '</button>' : '');
  };

  // opts.badge puts a small solid badge ("In your cart") at the right end of the name row.
  Sheet.prototype.pieceHtml = function (opts) {
    var head = '<h3 class="rdfu-piece__name">' + esc(opts.name) + '</h3>';
    if (opts.badge) head = '<div class="rdfu-piece__head">' + head + '<span class="rdfu-piece__badge">' + esc(opts.badge) + '</span></div>';
    return '<li class="rdfu-piece" data-piece="' + opts.key + '">' +
      '<div class="rdfu-piece__pic"><img src="' + esc(opts.img) + '" alt="" width="96" height="96"></div>' +
      '<div class="rdfu-piece__main">' + head + opts.body + '</div></li>';
  };

  Sheet.prototype.sizesHtml = function (key, list, selected, otherId) {
    var self = this;
    return '<div class="rdfu-sizes" role="group" aria-label="' + esc(key) + ' size">' + list.map(function (v) {
      var tee = key === 'top' ? String(v.id) : otherId;
      var shorts = key === 'top' ? otherId : String(v.id);
      var parent = tee && shorts ? self.parentFor(tee, shorts) : null;
      var off = !v.available || (tee && shorts && (!parent || !parent.available));
      if (self.mode === 'edit' && self.edit && String(v.id) === String(key === 'top' ? self.edit.teeId : self.edit.shortsId)) off = false;
      return '<button type="button" class="rdfu-size" data-size-' + key + '="' + v.id + '" aria-pressed="' + (String(v.id) === String(selected)) + '"' + (off ? ' disabled' : '') + '>' + esc(v.size) + '</button>';
    }).join('') + '</div>';
  };

  Sheet.prototype.render = function () {
    var d = this.data;
    var self = this;
    var choice = this.choices[this.pickChoice];
    var product = d.tops[choice.p];
    var colour = product.colours[choice.c];
    var html = '';

    // Top
    var topItem = this.inCart.top;
    var topInfo = topItem ? this.variants[String(topItem.variant_id)] : null;
    if (topItem && !this.changing.top) {
      html += this.pieceHtml({
        key: 'top',
        img: topInfo ? topInfo.img : line(topItem, ''),
        name: topInfo ? d.tops[topInfo.p].title : topItem.product_title,
        badge: 'In your cart',
        body: this.statusBlock(inCartStatus(topItem, topInfo), 'top', 'Change')
      });
    } else {
      var swatches = this.choices.map(function (ch, i) {
        var c = d.tops[ch.p].colours[ch.c];
        return '<button type="button" class="rdfu-swatch" data-swatch="' + i + '" aria-pressed="' + (i === self.pickChoice) + '" aria-label="' + esc(d.tops[ch.p].title + ', ' + c.name) + '" title="' + esc(d.tops[ch.p].title + ' · ' + c.name) + '"><img src="' + esc(c.img) + '" alt="" width="40" height="40"></button>';
      }).join('');
      html += this.pieceHtml({
        key: 'top',
        img: colour.img,
        name: product.title,
        badge: topItem ? 'In your cart' : '',
        body: (topItem ? this.statusBlock(inCartStatus(topItem, topInfo), 'top', 'Keep mine') : '') +
          '<p class="rdfu-piece__label">Colour <b>' + esc(colour.name) + '</b></p>' +
          '<div class="rdfu-swatches" role="group" aria-label="Top colour">' + swatches + '</div>' +
          '<p class="rdfu-piece__label">Size</p>' + this.sizesHtml('top', colour.variants, this.pickTop, this.shortsId())
      });
    }

    // Cap
    if (this.inCart.cap) {
      html += this.pieceHtml({ key: 'cap', img: d.cap.img, name: d.cap.title, badge: 'In your cart', body: this.statusBlock(inCartStatus(this.inCart.cap, null), '', '') });
    } else {
      html += this.pieceHtml({ key: 'cap', img: d.cap.img, name: d.cap.title, body: '<p class="rdfu-piece__status">One size</p>' });
    }

    // Shorts
    var sInfo = this.inCart.shorts ? this.variants[String(this.inCart.shorts.variant_id)] : null;
    if (this.inCart.shorts && !this.changing.shorts) {
      html += this.pieceHtml({ key: 'shorts', img: d.shorts.img, name: d.shorts.title, badge: 'In your cart', body: this.statusBlock(inCartStatus(this.inCart.shorts, sInfo), 'shorts', 'Change') });
    } else {
      html += this.pieceHtml({
        key: 'shorts',
        img: d.shorts.img,
        name: d.shorts.title,
        badge: this.inCart.shorts ? 'In your cart' : '',
        body: (this.inCart.shorts ? this.statusBlock(inCartStatus(this.inCart.shorts, sInfo), 'shorts', 'Keep mine') : '') +
          '<p class="rdfu-piece__label">Size</p>' + this.sizesHtml('shorts', d.shorts.variants, this.pickShorts, this.topId())
      });
    }

    var scrollTop = this.body ? this.body.scrollTop : 0;
    this.piecesEl.removeAttribute('aria-busy');
    this.piecesEl.innerHTML = html;
    if (this.body) this.body.scrollTop = scrollTop;
    this.paintTotals();
  };

  Sheet.prototype.paintTotals = function () {
    var p = this.pricing();
    // With a Superset saving lost the real saving is smaller: "Extra $10 off", as on the cart offer card.
    var loss = p.loss || 0;
    var saving = p.was > p.bundle ? p.was - p.bundle - (p.lossKnown ? loss : 0) : 0;
    if (saving < 0) saving = 0;
    var savingText = '';
    if (loss > 0 && !p.lossKnown) savingText = p.was > p.bundle ? 'Bundle saving' : '';
    else if (saving) savingText = (loss > 0 ? 'Extra ' + money(saving) + ' off' : 'Save ' + money(saving));
    var edit = this.mode === 'edit';
    this.subEl.textContent = edit
      ? 'Change the colour or size of your top or shorts. The cap is one size.' + (savingText ? ' ' + savingText + '.' : '')
      : 'Your top, the cap and the shorts as one bundle.' + (savingText ? ' ' + savingText + '.' : '');
    this.savingEl.textContent = savingText;
    this.savingEl.hidden = !saving;
    this.wasEl.textContent = saving ? money(p.was) : '';
    this.wasEl.hidden = !saving;
    this.nowEl.textContent = p.bundle ? money(p.bundle) : '';
    this.ctaLabel.textContent = this.busy ? (edit ? 'Saving' : 'Upgrading') : (edit ? 'Save changes' : 'Upgrade');
    var tail = '';
    if (p.parent && !this.busy) {
      if (edit) tail = p.delta > 0 ? '+' + money(p.delta) : (p.delta < 0 ? '\u2212' + money(-p.delta) : '');
      else tail = '+' + money(p.extra);
    }
    this.ctaPrice.textContent = tail;
    this.cta.classList.toggle('is-locked', !p.ready && !this.busy);
    this.cta.classList.toggle('is-busy', this.busy);
    this.cta.setAttribute('aria-disabled', p.ready && !this.busy ? 'false' : 'true');
    this.cta.setAttribute('aria-busy', this.busy ? 'true' : 'false');
    this.panel.classList.toggle('is-busy', this.busy);
    this.current = p;
  };

  // Shown the moment the sheet opens, until /cart.js answers.
  Sheet.prototype.paintLoading = function () {
    var row = '<li class="rdfu-piece is-skeleton" aria-hidden="true"><div class="rdfu-piece__pic"></div><div class="rdfu-piece__main"><span class="rdfu-skel rdfu-skel--name"></span><span class="rdfu-skel rdfu-skel--line"></span></div></li>';
    this.piecesEl.innerHTML = row + row + row;
    this.piecesEl.setAttribute('aria-busy', 'true');
    this.subEl.textContent = this.mode === 'edit' ? 'Change the colour or size of your top or shorts. The cap is one size.' : 'Your top, the cap and the shorts as one bundle.';
    this.savingEl.hidden = true;
    this.wasEl.hidden = true;
    this.nowEl.textContent = '';
    this.ctaLabel.textContent = this.mode === 'edit' ? 'Save changes' : 'Upgrade';
    this.ctaPrice.textContent = '';
    this.cta.classList.add('is-locked');
    this.cta.classList.remove('is-busy');
    this.cta.setAttribute('aria-disabled', 'true');
    this.cta.setAttribute('aria-busy', 'false');
    this.panel.classList.remove('is-busy');
    this.setMessage('');
  };

  Sheet.prototype.setMessage = function (text) {
    if (!this.msgEl) return;
    this.msgEl.textContent = text || '';
    this.msgEl.hidden = !text;
  };

  Sheet.prototype.nudge = function () {
    var key = !this.topId() ? 'top' : 'shorts';
    var row = this.piecesEl.querySelector('[data-piece="' + key + '"]');
    this.setMessage(key === 'top' ? 'Pick a size for your top.' : 'Pick a size for the shorts.');
    if (!row) return;
    row.classList.remove('is-nudge');
    void row.offsetWidth;
    row.classList.add('is-nudge');
  };

  /* ---------- Open, close, slide ---------- */

  // Slides in at once and fills the pieces in when the cart read (prefetched on hover or touch) arrives.
  Sheet.prototype.open = function () { return this.start('upgrade', null); };
  Sheet.prototype.openEdit = function (ref) { return this.start('edit', ref); };

  Sheet.prototype.start = function (mode, ref) {
    var self = this;
    if (!this.dialog || this.isOpen) return Promise.resolve(false);
    var active = document.activeElement;
    this.opener = active && active !== document.body ? active : null;
    this.mode = mode;
    this.edit = null;
    this.busy = false;
    this.loading = true;
    this.inCart = { top: null, cap: null, shorts: null };
    this.cartItems = [];
    if (this.titleEl) this.titleEl.textContent = mode === 'edit' ? 'Edit your Full Stack' : 'The Full Stack';
    this.seq += 1;
    var seq = this.seq;
    this.paintLoading();
    this.present();
    return takeCart().then(function (cart) {
      if (seq !== self.seq || !self.isOpen) return true;
      self.loading = false;
      if (mode === 'edit') {
        var problem = self.readEdit(cart, ref);
        if (problem) {
          self.piecesEl.removeAttribute('aria-busy');
          self.piecesEl.innerHTML = '';
          self.setMessage(problem);
          refreshCartDrawer().catch(function () { /* the message stands */ });
          return false;
        }
      } else {
        self.readCart(cart);
      }
      self.reset();
      self.render();
      return true;
    }, function (error) {
      if (seq !== self.seq || !self.isOpen) return false;
      self.loading = false;
      self.piecesEl.removeAttribute('aria-busy');
      self.piecesEl.innerHTML = '';
      self.setMessage('Could not read your cart. Close this and try again.');
      return false;
    });
  };

  // Edit mode: find the Full Stack line being edited and the pieces inside it. Returns a message on failure.
  Sheet.prototype.readEdit = function (cart, ref) {
    var lines = (cart && cart.items) || [];
    var bundleId = String(this.data.bundle.id);
    var line = lines.filter(function (item) { return ref && item.key === ref.key; })[0] ||
      lines.filter(function (item) { return String(item.product_id) === bundleId && ref && String(item.variant_id) === String(ref.variant); })[0];
    if (!line) return 'That Full Stack is no longer in your cart.';
    var row = this.rowByBundle[String(line.variant_id)];
    if (!row) return 'Could not read this Full Stack. Remove it and build it again.';
    this.cartItems = lines;
    this.edit = {
      key: line.key,
      variantId: String(line.variant_id),
      qty: Math.max(1, parseInt(line.quantity, 10) || 1),
      props: line.properties || {},
      teeId: String(row[0]),
      shortsId: String(row[1]),
      parentId: String(row[2])
    };
    return '';
  };

  Sheet.prototype.present = function () {
    this.isOpen = true;
    lockScroll();
    this.dialog.classList.remove('is-open');
    this.panel.style.transition = '';
    this.panel.style.transform = '';
    if (this.scrim) this.scrim.style.opacity = '';
    try { if (!this.dialog.open) this.dialog.showModal(); } catch (e) { this.dialog.setAttribute('open', ''); }
    if (this.body) this.body.scrollTop = 0;
    void this.panel.getBoundingClientRect();
    this.dialog.classList.add('is-open');
    this.slide(true);
    this.panel.focus({ preventScroll: true });
  };

  Sheet.prototype.close = function (opts) {
    opts = opts || {};
    if (!this.isOpen) return;
    var self = this;
    var opener = this.opener;
    var finished = false;
    var timer = null;
    this.isOpen = false;
    this.opener = null;
    this.seq += 1;
    var fromTransform = window.getComputedStyle(this.panel).transform;
    this.panel.style.transform = '';
    if (this.scrim) this.scrim.style.opacity = '';
    this.dialog.classList.remove('is-open');
    var finish = function () {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      if (self.isOpen) { unlockScroll(); return; }
      self.closing = true;
      if (self.dialog.open) {
        try { self.dialog.close(); } catch (e) { self.dialog.removeAttribute('open'); }
      }
      self.closing = false;
      unlockScroll();
      if (opts.returnFocus !== false && opener && opener.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
    };
    var anim = this.slide(false, fromTransform);
    if (anim) anim.onfinish = finish;
    timer = window.setTimeout(finish, CLOSE_MS + 80);
  };

  // Same slide as the Superset builder (one even ease, no overshoot, Web Animations API), a little quicker.
  Sheet.prototype.slide = function (opening, fromTransform) {
    var off = phoneQuery.matches ? 'translate3d(0, 100%, 0)' : 'translate3d(100%, 0, 0)';
    var on = 'translate3d(0, 0, 0)';
    var duration = opening ? OPEN_MS : CLOSE_MS;
    var easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
    if (this.panelAnim) this.panelAnim.cancel();
    if (this.scrimAnim) this.scrimAnim.cancel();
    this.panel.style.transition = 'none';
    if (this.scrim) this.scrim.style.transition = 'none';
    if (!this.panel.animate) return null;
    var start = opening ? off : (fromTransform && fromTransform !== 'none' ? fromTransform : on);
    this.panelAnim = this.panel.animate([{ transform: start }, { transform: opening ? on : off }], { duration: duration, easing: easing, fill: opening ? 'none' : 'forwards' });
    if (this.scrim) this.scrimAnim = this.scrim.animate([{ opacity: opening ? 0 : 1 }, { opacity: opening ? 1 : 0 }], { duration: duration, easing: 'ease', fill: opening ? 'none' : 'forwards' });
    var self = this;
    if (opening) {
      this.panelAnim.onfinish = function () {
        self.panel.style.transition = '';
        if (self.scrim) self.scrim.style.transition = '';
      };
    }
    return this.panelAnim;
  };

  /* ---------- Upgrade ---------- */

  Sheet.prototype.announce = function (text) {
    if (this.liveEl) this.liveEl.textContent = text || '';
  };

  Sheet.prototype.upgrade = function () {
    var self = this;
    if (this.busy || this.loading) return;
    var p = this.pricing();
    this.current = p;
    if (!p.ready) { this.nudge(); return; }
    this.busy = true;
    this.setMessage('');
    this.paintTotals();
    this.announce('Upgrading your cart');

    var operationId = uuid();
    var variantId = Number(p.parent.id);
    var panel = this.panel;
    var offerId = panel.getAttribute('data-rd-offer') || 'fullstack-upgrade';
    var placement = panel.getAttribute('data-rd-offer-placement') || 'cart_drawer';
    var properties = { _rd_offer_id: offerId, _rd_offer_version: 'v1', _rd_offer_placement: placement };
    var sections = sectionIds();
    var base = { sections: sections, sections_url: sectionsUrl() };
    var updateUrl = ajaxUrl('cart_update_url', '/cart/update.js');
    var addUrl = ajaxUrl('cart_add_url', '/cart/add.js');

    // One unit comes off each loose line that goes into the bundle.
    var updates = {};
    ['top', 'cap', 'shorts'].forEach(function (k) {
      var old = self.inCart[k];
      if (old) updates[old.key] = Math.max(0, (parseInt(old.quantity, 10) || 0) - 1);
    });
    var hasRemovals = Object.keys(updates).length > 0;
    var fast = !this.hasBundleLine(variantId);

    var request;
    if (fast) {
      // One call: adds the bundle variant, trims the loose lines, returns the rendered sections.
      var combined = {}; Object.keys(updates).forEach(function (key) { combined[key] = updates[key]; });
      combined[String(variantId)] = 1;
      request = post(updateUrl, Object.assign({ updates: combined }, base));
    } else if (hasRemovals) {
      request = post(addUrl, { id: variantId, quantity: 1, properties: properties }).then(function () {
        return post(updateUrl, Object.assign({ updates: updates }, base));
      });
    } else {
      request = post(addUrl, Object.assign({ id: variantId, quantity: 1, properties: properties }, base));
    }

    // Past this point the cart is already upgraded: nothing below may report a failure.
    var landed = function (state) {
      prefetched = null;
      var painted = false;
      try { painted = renderCart(state); } catch (e) { painted = false; }
      var repaint = painted ? Promise.resolve() : refreshCartDrawer().catch(function () { /* the next cart read shows it */ });
      try {
        document.dispatchEvent(new CustomEvent('rd:offer-added', { detail: {
          offerId: offerId, placement: placement, offerType: 'bundle', variantId: variantId, operationId: operationId,
          cartTotalCents: state && typeof state.total_price === 'number' ? state.total_price : undefined
        } }));
      } catch (e) { /* analytics must never block the cart */ }
      return repaint.then(function () {
        self.busy = false;
        self.paintTotals();
        self.announce('Upgraded to The Full Stack');
        self.close({ returnFocus: false });
        if (fast) tagBundleLine(state, variantId, properties);
      });
    };
    var failed = function (err) {
      prefetched = null;
      self.busy = false;
      self.setMessage(errorText(err));
      self.announce('The upgrade did not go through');
      self.paintTotals();
      // The drawer and this sheet show what is really in the cart now.
      refreshCartDrawer().then(function (cart) {
        if (!self.isOpen) return;
        self.readCart(cart);
        self.render();
      }).catch(function () { /* keep the message */ });
    };
    request.then(landed, failed).then(null, function () { self.busy = false; });
  };

  Sheet.prototype.submit = function () {
    if (this.mode === 'edit') this.save();
    else this.upgrade();
  };

  // Edit mode save: one /cart/update.js swaps the old bundle line for the new variant, same quantity.
  Sheet.prototype.save = function () {
    var self = this;
    if (this.busy || this.loading || !this.edit) return;
    var p = this.pricing();
    this.current = p;
    if (!p.ready) { if (!this.topId() || !this.shortsId()) this.nudge(); return; }
    this.busy = true;
    this.setMessage('');
    this.paintTotals();
    this.announce('Saving your Full Stack');

    var e = this.edit;
    var newId = String(p.parent.id);
    var base = { sections: sectionIds(), sections_url: sectionsUrl() };
    // If the cart already holds a line of the new variant, add to that line (a variant id key would set it instead).
    var existing = this.cartItems.filter(function (item) { return String(item.variant_id) === newId && item.key !== e.key; })[0];
    var updates = {};
    updates[e.key] = 0;
    if (existing) updates[existing.key] = (parseInt(existing.quantity, 10) || 0) + e.qty;
    else updates[newId] = e.qty;

    var landed = function (state) {
      prefetched = null;
      var painted = false;
      try { painted = renderCart(state); } catch (err) { painted = false; }
      var repaint = painted ? Promise.resolve() : refreshCartDrawer().catch(function () { /* the next cart read shows it */ });
      return repaint.then(function () {
        self.busy = false;
        self.paintTotals();
        self.announce('Full Stack saved');
        self.close({ returnFocus: false });
        if (!existing) tagBundleLine(state, newId, e.props);
      });
    };
    var failed = function (err) {
      prefetched = null;
      self.busy = false;
      self.setMessage(errorText(err));
      self.announce('The change did not go through');
      self.paintTotals();
      refreshCartDrawer().then(function (cart) {
        if (!self.isOpen) return;
        self.cartItems = (cart && cart.items) || [];
        var still = self.cartItems.some(function (item) { return item.key === e.key; });
        if (!still) { self.setMessage('That Full Stack changed in your cart. Close this and open it again.'); self.edit = null; self.paintTotals(); }
      }).catch(function () { /* keep the message */ });
    };
    post(ajaxUrl('cart_update_url', '/cart/update.js'), Object.assign({ updates: updates }, base)).then(landed, failed).then(null, function () { self.busy = false; });
  };

  // The x on a piece: the bundle line goes and the other two pieces come back as separate items (one /cart/update.js).
  Sheet.prototype.split = function (ref) {
    var self = this;
    if (this.splitting) return Promise.resolve(false);
    this.splitting = true;
    var holder = ref.el;
    var setBusy = function (on) {
      if (!holder || !holder.isConnected) return;
      holder.classList.toggle('is-rdfu-busy', on);
      holder.setAttribute('aria-busy', on ? 'true' : 'false');
      if (!on) Array.prototype.forEach.call(holder.querySelectorAll('.is-busy'), function (node) { node.classList.remove('is-busy'); });
    };
    setBusy(true);
    showBundleMessage(ref.key, '');
    var base = { sections: sectionIds(), sections_url: sectionsUrl() };
    var done = function () { self.splitting = false; };
    var failed = function (err) {
      prefetched = null;
      done();
      setBusy(false);
      var text = errorText(err);
      refreshCartDrawer().then(function () { showBundleMessage(ref.key, text); }, function () { showBundleMessage(ref.key, text, holder); });
    };
    return takeCart().then(function (cart) {
      var lines = (cart && cart.items) || [];
      var bundleId = String(self.data.bundle.id);
      var line = lines.filter(function (item) { return item.key === ref.key; })[0] ||
        lines.filter(function (item) { return String(item.product_id) === bundleId && String(item.variant_id) === String(ref.variant); })[0];
      if (!line) throw { description: 'That Full Stack is no longer in your cart.' };
      var row = self.rowByBundle[String(line.variant_id)];
      if (!row) throw { description: 'Could not split this Full Stack. Remove it and add the pieces again.' };
      var ids = { top: String(row[0]), shorts: String(row[1]), cap: String(row[3]) };
      var qty = Math.max(1, parseInt(line.quantity, 10) || 1);
      var updates = {};
      updates[line.key] = 0;
      ['top', 'cap', 'shorts'].forEach(function (kind) {
        if (kind === ref.piece) return;
        var variant = ids[kind];
        // A loose line of that variant already in the cart gets the quantity added (a variant id key would set it).
        var loose = lines.filter(function (item) { return String(item.variant_id) === variant && item.key !== line.key && !item.has_components; })[0];
        if (loose) updates[loose.key] = (parseInt(loose.quantity, 10) || 0) + qty;
        else updates[variant] = qty;
      });
      return post(ajaxUrl('cart_update_url', '/cart/update.js'), Object.assign({ updates: updates }, base));
    }).then(function (state) {
      prefetched = null;
      done();
      var painted = false;
      try { painted = renderCart(state); } catch (err) { painted = false; }
      if (!painted) return refreshCartDrawer().catch(function () { setBusy(false); });
      return state;
    }, failed);
  };

  function showBundleMessage(key, text, fallbackHolder) {
    var holders = document.querySelectorAll('[data-rdfu-bundle]');
    var holder = null;
    for (var i = 0; i < holders.length; i += 1) {
      if (holders[i].getAttribute('data-rdfu-line') === key) { holder = holders[i]; break; }
    }
    holder = holder || fallbackHolder;
    var msg = holder && holder.querySelector('[data-rdfu-msg]');
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
  }

  // The offer properties (analytics) go on the new bundle line after the cart is already showing it.
  function tagBundleLine(state, variantId, properties) {
    var line = ((state && state.items) || []).filter(function (item) {
      return String(item.variant_id) === String(variantId) && !(item.properties && Object.keys(item.properties).length);
    })[0];
    if (!line || !properties || !Object.keys(properties).length) return;
    var changeUrl = ajaxUrl('cart_change_url', '/cart/change.js');
    // change.js without a quantity resets the line to 1, so the quantity goes along with the properties.
    var quantity = Math.max(1, parseInt(line.quantity, 10) || 1);
    var send = function () { return post(changeUrl, { id: line.key, quantity: quantity, properties: properties }); };
    send().catch(function () {
      return new Promise(function (resolve) { window.setTimeout(resolve, 1200); }).then(send);
    }).then(function (cart) {
      // The line key changes with its properties: keep the controls in the drawer pointing at the new key.
      var fresh = ((cart && cart.items) || []).filter(function (item) {
        return String(item.variant_id) === String(variantId) && item.properties && Object.keys(item.properties).length;
      })[0];
      if (!fresh) return;
      Array.prototype.forEach.call(document.querySelectorAll('[data-rdfu-bundle]'), function (holder) {
        if (holder.getAttribute('data-rdfu-line') === line.key) holder.setAttribute('data-rdfu-line', fresh.key);
      });
    }).catch(function () { /* attribution only: the cart itself is already correct */ });
  }

  /* ---------- Events ---------- */

  Sheet.prototype.on = function (target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.handlers.push([target, type, handler, options]);
  };

  Sheet.prototype.bind = function () {
    var self = this;
    this.body = this.root.querySelector('[data-rdfu-body]');
    this.on(this.dialog, 'click', function (event) {
      if (event.target === self.scrim) { self.close(); return; }
      var button = event.target.closest && event.target.closest('button');
      if (!button || !self.dialog.contains(button)) return;
      if (button.hasAttribute('data-rdfu-close')) { self.close(); return; }
      if (button === self.cta) { self.submit(); return; }
      if (self.busy || self.loading) return;
      if (button.dataset.pc) {
        var piece = button.dataset.pc;
        self.changing[piece] = !self.changing[piece];
        if (piece === 'top' && self.inCart.top) {
          var cartTop = self.variants[String(self.inCart.top.variant_id)];
          if (cartTop) {
            for (var t = 0; t < self.choices.length; t += 1) {
              if (self.choices[t].p === cartTop.p && self.choices[t].c === cartTop.c) { self.pickChoice = t; break; }
            }
          }
          self.pickTop = self.changing.top ? String(self.inCart.top.variant_id) : '';
        }
        if (piece === 'shorts' && self.inCart.shorts) self.pickShorts = self.changing.shorts ? String(self.inCart.shorts.variant_id) : '';
        self.setMessage('');
        self.render();
        return;
      }
      if (button.dataset.swatch != null) {
        var keepSize = (self.mode === 'edit' || self.changing.top) && self.pickTop && self.variants[self.pickTop] ? self.variants[self.pickTop].size : '';
        self.pickChoice = Number(button.dataset.swatch);
        self.pickTop = '';
        if (keepSize) {
          // Same size in the new colour when it exists.
          var pickedChoice = self.choices[self.pickChoice];
          var same = self.data.tops[pickedChoice.p].colours[pickedChoice.c].variants.filter(function (v) { return v.size === keepSize && v.available; })[0];
          if (same) self.pickTop = String(same.id);
        }
        self.setMessage('');
        self.render();
        return;
      }
      if (button.dataset.sizeTop) {
        self.pickTop = self.pickTop === button.dataset.sizeTop ? '' : button.dataset.sizeTop;
        self.setMessage('');
        self.render();
        return;
      }
      if (button.dataset.sizeShorts) {
        self.pickShorts = self.pickShorts === button.dataset.sizeShorts ? '' : button.dataset.sizeShorts;
        self.setMessage('');
        self.render();
      }
    });
    this.on(this.dialog, 'keydown', function (event) {
      if (event.key === 'Escape') { event.preventDefault(); self.close(); }
    });
    this.on(this.dialog, 'cancel', function (event) { event.preventDefault(); self.close(); });
    this.on(this.dialog, 'close', function () {
      if (self.closing || !self.isOpen) return;
      var opener = self.opener;
      self.isOpen = false;
      self.opener = null;
      self.dialog.classList.remove('is-open');
      unlockScroll();
      if (opener && opener.isConnected) opener.focus({ preventScroll: true });
    });
    this.bindDrag();
  };

  // Phones: drag the header down to close, like the Superset sheet.
  Sheet.prototype.bindDrag = function () {
    var self = this;
    var area = this.root.querySelector('[data-rdfu-drag]');
    if (!area) return;
    var startY = 0, lastY = 0, lastT = 0, speed = 0, tracking = false, moved = false;
    this.on(area, 'touchstart', function (event) {
      if (!phoneQuery.matches || !self.isOpen || event.touches.length !== 1) return;
      tracking = true; moved = false;
      startY = lastY = event.touches[0].clientY;
      lastT = event.timeStamp; speed = 0;
    }, { passive: true });
    this.on(area, 'touchmove', function (event) {
      if (!tracking) return;
      var y = event.touches[0].clientY;
      var dy = Math.max(0, y - startY);
      if (!moved && dy < 6) return;
      if (!moved) {
        moved = true;
        self.panel.style.transition = 'none';
        if (self.scrim) self.scrim.style.transition = 'none';
      }
      var dt = Math.max(1, event.timeStamp - lastT);
      speed = (y - lastY) / dt;
      lastY = y; lastT = event.timeStamp;
      self.panel.style.transform = 'translate3d(0,' + dy + 'px,0)';
      if (self.scrim) self.scrim.style.opacity = String(Math.max(0, 1 - dy / Math.max(1, self.panel.offsetHeight)));
    }, { passive: true });
    var end = function () {
      if (!tracking) return;
      tracking = false;
      if (!moved) return;
      var dy = Math.max(0, lastY - startY);
      self.panel.style.transition = '';
      if (self.scrim) self.scrim.style.transition = '';
      if (dy > 120 || (dy > 40 && speed > 0.5)) {
        self.close();
      } else {
        self.panel.style.transform = '';
        if (self.scrim) self.scrim.style.opacity = '';
      }
    };
    this.on(area, 'touchend', end);
    this.on(area, 'touchcancel', end);
  };

  /* ---------- Page wiring ---------- */

  function sheet() {
    if (root && root.root.isConnected) return root;
    var el = document.querySelector('[data-rd-fullstack-upgrade]');
    root = el ? new Sheet(el) : null;
    return root && root.dialog && root.data ? root : null;
  }

  window.RDFullStackUpgrade = {
    version: 1,
    // false right away when the sheet is not on the page; otherwise a promise.
    open: function () {
      var s = sheet();
      if (!s) return false;
      return s.open();
    },
    close: function () {
      var s = sheet();
      if (s) s.close();
    },
    // Edit mode: ref = { key, variant } of a Full Stack line. false right away when the sheet is not on the page.
    edit: function (ref) {
      var s = sheet();
      if (!s) return false;
      return s.openEdit(ref || {});
    }
  };

  // Change and x controls on the Full Stack line (cart drawer and cart page markup carry data-rdfu-bundle).
  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest ? event.target.closest('[data-rdfu-change],[data-rdfu-split]') : null;
    if (!target) return;
    var holder = target.closest('[data-rdfu-bundle]');
    var s = sheet();
    if (!holder || !s) return;
    event.preventDefault();
    var ref = { key: holder.getAttribute('data-rdfu-line'), variant: holder.getAttribute('data-rdfu-variant'), el: holder };
    if (target.hasAttribute('data-rdfu-change')) {
      ref.piece = target.getAttribute('data-rdfu-change');
      s.openEdit(ref);
    } else {
      ref.piece = target.getAttribute('data-rdfu-split');
      target.classList.add('is-busy');
      s.split(ref);
    }
  });

  // The controls stay hidden unless this script is on the page (see rd-cart-drawer-v2.css).
  if (document.querySelector('[data-rd-fullstack-upgrade]')) document.documentElement.classList.add('rdfu-on');
})();
